import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

// Api
import { PreviewUpgrade, ApplyUpgrade } from "../../Api/Stripe";

// Models
import type Plan from "../../Models/DTOs/Plan";
import type { UserPaymentMethod } from "../../Models/Interfaces/UserPaymentMethod";

// Helpers
import {getStripePriceId} from "../../Helpers/Currency/Stripe";
import {getResolvedOffer, isPrepaidOffer} from "../../Helpers/BillingOffers";

export function useUpgradePreview(
    params: {
        userId?: string;
        subscriptionCadence?: "month" | "year" | string | undefined;
        isActive?: boolean;
        defaultPM?: UserPaymentMethod | null;
        selectedPlan?: Plan | null;
        auto?: boolean; // default true
        getValidAccessToken?: () => Promise<string | null>;
    }
) {
    const { userId, subscriptionCadence, isActive, defaultPM, selectedPlan, auto = true, getValidAccessToken } = params,

        [priceMinor, setPriceMinor] = useState<number | null>(null),
        [currency, setCurrency] = useState<string | null>(null),
        [error, setError] = useState<string>(""),
        [loading, setLoading] = useState(false),
        [needsPM, setNeedsPM] = useState(false),
        [planKey, setPlanKey] = useState<string>(""),

        guardRef = useRef(false),
        priceId = useMemo(() => getStripePriceId(selectedPlan), [selectedPlan]),
        offer = useMemo(() => getResolvedOffer(selectedPlan), [selectedPlan]),
        offerKey = offer?.offer_key,
        targetPlanKey = selectedPlan?.key ?? "",

        canPreview =
            !!userId &&
            isActive &&
            subscriptionCadence?.toLowerCase() === "month" &&
            !!targetPlanKey &&
            !isPrepaidOffer(offer),

        run = async () => {
            if (!canPreview) return;
            if (!defaultPM) {
                setNeedsPM(true);
                return;
            }
            if (guardRef.current) return;
            guardRef.current = true;

            const ac = new AbortController();
            setLoading(true);
            setError("");
            try {
                // upgrade endpoint is self-or-admin protected — attach a valid token
                const token = getValidAccessToken ? await getValidAccessToken() : null;
                if (!token) {
                    setError("Your session has expired. Please log in again.");
                    return;
                }
                const res = await PreviewUpgrade(
                    userId!,
                    {
                        targetPlanKey,
                        offerKey,
                        offerId: offer?.id,
                        offerVersion: offer?.version,
                        finalAmountMinor: offer?.final_amount_minor,
                        legacyPriceId: offerKey || selectedPlan?.offerCatalogResolved
                            ? undefined
                            : priceId,
                    },
                    token,
                    ac
                );
                if (res?.preview) {
                    setPriceMinor(res.preview.amount_due ?? 0);
                    setCurrency(res.preview.currency ?? "gbp");
                    setPlanKey(res.preview.plan_key);
                }
            } catch (e: any) {
                if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED") return;
                setError(e?.message || "Could not preview upgrade.");
                // if backend complained about missing PM, keep PM form open
                setNeedsPM(true);
            } finally {
                setLoading(false);
                guardRef.current = false;
            }
            return () => ac.abort();
        };

    useEffect(() => {
        if (!auto) return;
        // auto-run on mount/changes if eligible
        void run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userId,
        subscriptionCadence,
        isActive,
        defaultPM,
        priceId,
        offerKey,
        offer?.id,
        offer?.version,
        offer?.final_amount_minor,
        targetPlanKey,
    ]);

    return {
        priceMinor,
        currency,
        loading,
        error,
        needsPM,
        refresh: run,
        priceId,
        planKey,
        offerKey,
        targetPlanKey,
    };
}

// ---------- apply hook ----------
export function useApplyUpgrade(
    userId?: string,
    getValidAccessToken?: () => Promise<string | null>
) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const apply = async (
        targetPlanKey: string,
        offerKey?: string,
        offerId?: number,
        offerVersion?: number,
        finalAmountMinor?: number,
        legacyPriceId?: string
    ) => {
        if (!userId) return { applied: false };

        // prevent concurrent requests at the hook level
        if (loading) return { applied: false };

        setLoading(true);
        setError("");
        const ac = new AbortController();

        try {
            // upgrade endpoint is self-or-admin protected — attach a valid token
            const token = getValidAccessToken ? await getValidAccessToken() : null;
            if (!token) {
                setError("Your session has expired. Please log in again.");
                return { applied: false };
            }
            const res = await ApplyUpgrade(
                userId,
                {
                    targetPlanKey,
                    offerKey,
                    offerId,
                    offerVersion,
                    finalAmountMinor,
                    legacyPriceId: offerKey ? undefined : legacyPriceId,
                },
                token,
                ac
            );
            return res;
        } catch (e: any) {
            setError(e?.message || "Upgrade failed.");
            return { applied: false };
        } finally {
            setLoading(false);
        }
    };

    return { apply, loading, error };
}
